import { Component } from '@theme/component';

const COUNTRY_CACHE_KEY = 'lode:detected-country';

/**
 * "Ships by [date]" + "Fast [country] Shipping" strip (blocks/shipping-eta.liquid).
 * Recalculates the ship date in the store's time zone on every view, since the page itself may be cached, and
 * shows the country matched to the shopper's IP via Shopify's /browsing_context_suggestions.json.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [ship] - Ship date text.
 * @property {HTMLElement} [countryWrap] - Wrapper of the country lines.
 * @property {HTMLElement[]} [countries] - One line per country the store sells to (data-country = ISO code).
 *
 * @extends Component<Refs>
 */
class ShippingEtaComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#updateDate();
    this.#detectCountry();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateDate();
    this.#detectCountry();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #updateDate() {
    if (this.hasAttribute('data-fixed')) return;
    const target = this.refs.ship?.querySelector('[data-ship-date]');
    if (!target) return;

    // Store-local "now" expressed with UTC getters: shift by the store's offset (e.g. -0700).
    const offset = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.utcOffset ?? '');
    const offsetMinutes = offset ? (offset[1] === '-' ? -1 : 1) * (Number(offset[2]) * 60 + Number(offset[3])) : 0;
    const now = new Date(Date.now() + offsetMinutes * 60000);
    const skipWeekends = this.dataset.skipWeekends === 'true';
    const isWeekend = (/** @type {Date} */ date) => skipWeekends && (date.getUTCDay() === 0 || date.getUTCDay() === 6);

    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    if (now.getUTCHours() >= Number(this.dataset.cutoffHour ?? 24)) date.setUTCDate(date.getUTCDate() + 1);
    let remaining = Number(this.dataset.processingDays) || 0;
    for (let guard = 0; guard < 40; guard += 1) {
      if (isWeekend(date)) {
        date.setUTCDate(date.getUTCDate() + 1);
        continue;
      }
      if (remaining <= 0) break;
      remaining -= 1;
      date.setUTCDate(date.getUTCDate() + 1);
    }

    target.textContent = new Intl.DateTimeFormat(this.dataset.locale || undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  async #detectCountry() {
    if (!this.hasAttribute('data-detect-country')) return;
    const { countries = [], countryWrap } = this.refs;
    if (!countries.length || !countryWrap) return;

    let code = sessionStorage.getItem(COUNTRY_CACHE_KEY);
    if (!code) {
      this.#controller?.abort();
      this.#controller = new AbortController();
      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root ?? '/'}browsing_context_suggestions.json?country[enabled]=true`,
          { signal: this.#controller.signal }
        );
        if (!response.ok) return;
        /** @type {{ detected_values?: { country?: { handle?: string } } }} */
        const data = await response.json();
        code = data.detected_values?.country?.handle ?? '';
        if (code) sessionStorage.setItem(COUNTRY_CACHE_KEY, code);
      } catch {
        return;
      }
    }
    if (!code) return;

    const match = countries.find((item) => item.dataset.country === code);
    // A country the store doesn't sell to gets no "fast shipping" promise.
    countryWrap.hidden = !match;
    for (const item of countries) item.hidden = item !== match;
  }
}

if (!customElements.get('shipping-eta-component')) customElements.define('shipping-eta-component', ShippingEtaComponent);
